import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import style from '../styles/contact.module.css';
import {
  FaEnvelope, FaPhone, FaMapMarkerAlt, FaArrowRight, FaDirections, FaChevronRight,
  FaCheckCircle, FaExclamationTriangle, FaSpinner
} from 'react-icons/fa';

/* ── Location (single source of truth) ── */
const MAP_QUERY  = '12.832158,77.5700629';
/* must be maps.google.com/...&output=embed — www.google.com/maps refuses to render in an iframe */
const MAP_EMBED  = `https://maps.google.com/maps?q=${MAP_QUERY}&z=16&hl=en&output=embed`;
const MAP_LINK   = `https://www.google.com/maps?q=${MAP_QUERY}&z=17&hl=en`;
const DIRECTIONS = `https://www.google.com/maps/dir/?api=1&destination=${MAP_QUERY}`;

const Contact = () => {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', service: '', message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  // Auto-dismiss success/error messages after 5 seconds
  useEffect(() => {
    if (submitStatus) {
      const timer = setTimeout(() => setSubmitStatus(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [submitStatus]);

  const validateForm = () => {
    const newErrors = {};
    
    // Name validation
    if (!form.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (form.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!form.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(form.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    // Phone validation (optional, but if provided must be valid)
    if (form.phone.trim()) {
      const phoneRegex = /^[\d\s+()-]{10,}$/;
      if (!phoneRegex.test(form.phone)) {
        newErrors.phone = 'Please enter a valid phone number';
      }
    }

    // Service validation
    if (!form.service) {
      newErrors.service = 'Please select a service';
    }

    // Message validation
    if (!form.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (form.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // TODO: Replace with your actual API endpoint
      // const response = await fetch('/api/contact', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(form)
      // });

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Simulate success
      setSubmitStatus('success');
      setForm({ name: '', email: '', phone: '', service: '', message: '' });
      
      // Optional: Log or send to analytics
      console.log('Form submitted:', form);

    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>

      {/* ── Hero banner ── */}
      <section className={style.heroSection}>
        <span className={style.heroOverlay} />
        <span className={style.heroGlowA} />
        <span className={style.heroGlowB} />

        {/* Floating particles */}
        <div className={style.heroParticles} aria-hidden="true">
          <span className={style.heroPart1} />
          <span className={style.heroPart2} />
          <span className={style.heroPart3} />
          <span className={style.heroPart4} />
          <span className={style.heroPart5} />
        </div>

        <div className={`container ${style.heroInner}`}>
          <div className={style.heroContent}>
            <nav className={style.breadcrumb}>
              <Link to="/">Home</Link>
              <FaChevronRight className={style.breadcrumbSep} />
              <span>Contact</span>
            </nav>

            <p className={style.heroEyebrow}>Get In Touch</p>
            <h1 className={style.heroTitle}>
              <span className={style.plain}>Contact </span>
              <span className={style.accent}>Us</span>
            </h1>
            <p className={style.heroSubtitle}>
              Have an e-waste or metal scrap requirement? Talk to our team and we&rsquo;ll help
              you dispose of it responsibly — safely, legally and sustainably.
            </p>

            {/* ── Quick-action pill buttons ── */}
            <div className={style.heroActions}>
              <a href="tel:+918762414394" className={style.heroActionBtn}>
                <FaPhone /> Call Us Now
              </a>
              <a href="mailto:kmkindustries22@gmail.com" className={style.heroActionBtnOutline}>
                <FaEnvelope /> Email Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Body ── */}
      <section className={style.contactBody}>
        <span className={style.bodyGlowA} />
        <span className={style.bodyGlowB} />

        <div className={`container ${style.bodyInner}`}>

          {/* Section heading */}
          <header className={style.sectionHead} data-aos="fade-up">
            <span className={style.sectionEyebrow}>We&rsquo;re here to help</span>
            <h2 className={style.sectionTitle}>Get in Touch</h2>
            <p className={style.sectionDesc}>
              Send us a message using the form, or reach us directly through the details
              alongside. Our team replies within 24 hours on business days.
            </p>
          </header>

          <div className={style.contactGrid}>

            {/* ── Form card ── */}
            <div className={style.formPanel} data-aos="fade-right" data-aos-duration="800">
              <div className={style.formHead}>
                <h3>Send Us a Message</h3>
                <p>Fill in the form and our team will get back to you within 24 hours.</p>
              </div>

              {/* ── Submit status banners ── */}
              {submitStatus === 'success' && (
                <div className={style.alertSuccess} role="alert">
                  <FaCheckCircle className={style.alertIcon} />
                  <div>
                    <strong>Message sent!</strong>
                    <span>We&rsquo;ll get back to you within 24 hours.</span>
                  </div>
                  <button className={style.alertClose} onClick={() => setSubmitStatus(null)} aria-label="Dismiss">×</button>
                </div>
              )}
              {submitStatus === 'error' && (
                <div className={style.alertError} role="alert">
                  <FaExclamationTriangle className={style.alertIcon} />
                  <div>
                    <strong>Something went wrong.</strong>
                    <span>Please try again or call us directly.</span>
                  </div>
                  <button className={style.alertClose} onClick={() => setSubmitStatus(null)} aria-label="Dismiss">×</button>
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                <div className={style.formRow}>
                  <div className={`${style.formGroup} ${errors.name ? style.hasError : ''}`}>
                    <label htmlFor="name">Your Name <span className={style.required}>*</span></label>
                    <input
                      id="name" name="name" type="text" placeholder="e.g. Rajesh Mehta"
                      value={form.name} onChange={handleChange}
                      aria-describedby={errors.name ? 'name-error' : undefined}
                      aria-invalid={!!errors.name}
                    />
                    {errors.name && <span id="name-error" className={style.errorMsg}>{errors.name}</span>}
                  </div>
                  <div className={`${style.formGroup} ${errors.email ? style.hasError : ''}`}>
                    <label htmlFor="email">Email Address <span className={style.required}>*</span></label>
                    <input
                      id="email" name="email" type="email" placeholder="you@company.com"
                      value={form.email} onChange={handleChange}
                      aria-describedby={errors.email ? 'email-error' : undefined}
                      aria-invalid={!!errors.email}
                    />
                    {errors.email && <span id="email-error" className={style.errorMsg}>{errors.email}</span>}
                  </div>
                </div>

                <div className={style.formRow}>
                  <div className={`${style.formGroup} ${errors.phone ? style.hasError : ''}`}>
                    <label htmlFor="phone">Phone Number <span className={style.optional}>(optional)</span></label>
                    <input
                      id="phone" name="phone" type="tel" placeholder="+91 98765 43210"
                      value={form.phone} onChange={handleChange}
                      aria-describedby={errors.phone ? 'phone-error' : undefined}
                      aria-invalid={!!errors.phone}
                    />
                    {errors.phone && <span id="phone-error" className={style.errorMsg}>{errors.phone}</span>}
                  </div>
                  <div className={`${style.formGroup} ${errors.service ? style.hasError : ''}`}>
                    <label htmlFor="service">Service Required <span className={style.required}>*</span></label>
                    <select
                      id="service" name="service" value={form.service} onChange={handleChange}
                      aria-describedby={errors.service ? 'service-error' : undefined}
                      aria-invalid={!!errors.service}
                    >
                      <option value="">Select a service</option>
                      <option value="it">IT Assets Disposition</option>
                      <option value="metal">All Metal Disposition</option>
                      <option value="other">Other</option>
                    </select>
                    {errors.service && <span id="service-error" className={style.errorMsg}>{errors.service}</span>}
                  </div>
                </div>

                <div className={`${style.formGroup} ${style.messageGroup} ${errors.message ? style.hasError : ''}`}>
                  <label htmlFor="message">
                    Your Message <span className={style.required}>*</span>
                    <span className={style.charCount}>{form.message.length} / 500</span>
                  </label>
                  <textarea
                    id="message" name="message"
                    placeholder="Tell us about your requirements..."
                    value={form.message} onChange={handleChange}
                    maxLength={500}
                    aria-describedby={errors.message ? 'message-error' : undefined}
                    aria-invalid={!!errors.message}
                  />
                  {errors.message && <span id="message-error" className={style.errorMsg}>{errors.message}</span>}
                </div>

                <button type="submit" className={style.submitBtn} disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <FaSpinner className={style.spinnerIcon} /> Sending…
                    </>
                  ) : (
                    <>
                      Send Message <FaArrowRight className={style.submitArrow} />
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* ── Single contact card (details + map) ── */}
            <aside className={style.contactCard} data-aos="fade-left" data-aos-duration="800">
              <div className={style.contactCardHead}>
                <h3>Contact Information</h3>
                <p>Reach us through any of the channels below.</p>
              </div>

              <ul className={style.contactList}>
                <li>
                  <span className={style.contactIcon}><FaPhone /></span>
                  <div className={style.contactText}>
                    <h5>Phone</h5>
                    <a href="tel:+918762414394">+91 87624 14394</a>
                    <a href="tel:+919972474042">+91 99724 74042</a>
                  </div>
                </li>

                <li>
                  <span className={style.contactIcon}><FaEnvelope /></span>
                  <div className={style.contactText}>
                    <h5>Email</h5>
                    <a href="mailto:kmkindustries22@gmail.com">kmkindustries22@gmail.com</a>
                  </div>
                </li>

                <li>
                  <span className={style.contactIcon}><FaMapMarkerAlt /></span>
                  <div className={style.contactText}>
                    <h5>Address</h5>
                    <a href={MAP_LINK} target="_blank" rel="noreferrer">
                      #19, New No 13/9, Bilwaradahalli,<br />
                      Shanubhoganahalli Main Road,<br />
                      Jigani (H), Anekal (T),<br />
                      Bangalore – 560083
                    </a>
                  </div>
                </li>
              </ul>

              {/* Map sits at the bottom of the same card */}
              <div className={style.mapWrap}>
                <iframe
                  className={style.mapFrame}
                  title="KMK Industries location on Google Maps"
                  src={MAP_EMBED}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />

                <a
                  className={style.mapOverlay}
                  href={MAP_LINK}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Open the KMK Industries location in Google Maps"
                >
                  <span className={style.mapOverlayBtn}>
                    Open in Google Maps <FaArrowRight />
                  </span>
                </a>

                <a className={style.directionsBtn} href={DIRECTIONS} target="_blank" rel="noreferrer">
                  <FaDirections /> Get Directions
                </a>
              </div>
            </aside>

          </div>
        </div>
      </section>

    </div>
  );
};

export default Contact;
